/**
 * Searchable, paginated user column for matching (tutors, students, organizers).
 * Owns fetch, filters, and pagination; the parent only passes excludedIds.
 */

import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { GraduationCap, User, ChevronLeft, ChevronRight, Filter, Plus } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { Spinner } from '@/components/ui/spinner';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { FilterConfigurePanel } from '@/components/common/data-table/FilterConfigurePanel';
import { StudentFiltersContent } from '@/components/students/inperson/StudentFilters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  OrganizerFiltersContent,
  DEFAULT_ORGANIZER_FILTERS,
  type OrganizerFilters,
} from './OrganizerFilters';
import { useFilterVisibility } from '@/hooks/useFilterVisibility';
import { useUsersSearch } from '@/hooks/useUsersSearch';
import { cn } from '@/lib/utils';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import { getPrimaryRole } from '@/utils/primary-role-utils';
import type { UserWithProfile } from '@/types/user.types';
import { getUserRoleGroup } from '@/types/user.types';
import { availabilityKeysToDays } from '@/utils/common-utils';
import type { StudentFilters } from '@/types/inperson/student.types';

/** Filter ids shown on the matching tutors column. */
const MATCHING_TUTOR_FILTER_IDS = [
  'filter-status',
  'filter-role',
  'filter-subject',
  'filter-establishment',
  'filter-availability',
  'filter-entry-date',
  'filter-contract-end-date',
  'filter-exit-date',
  'filter-exit-reason',
] as const;

/** One filter id from the matching tutors column. */
type MatchingTutorFilterId = (typeof MATCHING_TUTOR_FILTER_IDS)[number];

/** French labels for matching-tutor filter ids. */
const MATCHING_TUTOR_FILTER_LABELS: Record<MatchingTutorFilterId, string> = {
  'filter-status': 'Statut',
  'filter-role': 'Rôle',
  'filter-subject': 'Matières',
  'filter-establishment': 'Établissement',
  'filter-availability': 'Disponibilité',
  'filter-entry-date': "Date d'entrée",
  'filter-contract-end-date': 'Date de fin engagement',
  'filter-exit-date': 'Date de désactivation',
  'filter-exit-reason': 'Motif de sortie',
};

/** Filter ids shown on the matching students column. */
const MATCHING_STUDENT_FILTER_IDS = [
  'filter-status',
  'filter-grade-level',
  'filter-subject',
  'filter-establishment',
  'filter-class',
  'filter-specialties',
  'filter-dates',
  'filter-is-minor',
  'filter-special-needs',
  'filter-accompaniment-type',
  'filter-availability',
  'filter-exit-date',
  'filter-exit-reason',
] as const;

/** One filter id from the matching students column. */
type MatchingStudentFilterId = (typeof MATCHING_STUDENT_FILTER_IDS)[number];

/** French labels for matching-student filter ids. */
const MATCHING_STUDENT_FILTER_LABELS: Record<MatchingStudentFilterId, string> = {
  'filter-status': 'Statut',
  'filter-grade-level': 'Niveau',
  'filter-subject': 'Matière',
  'filter-establishment': 'Établissement',
  'filter-class': 'Classe',
  'filter-specialties': 'Spécialités',
  'filter-dates': "Date d'entrée dans le dispositif",
  'filter-is-minor': 'Mineur',
  'filter-special-needs': 'Besoins spécifiques',
  'filter-accompaniment-type': "Type d'accompagnement",
  'filter-availability': 'Disponibilité',
  'filter-exit-date': 'Date de désactivation',
  'filter-exit-reason': 'Motif de sortie',
};

/** Props for {@link UsersListColumn}. */
interface UsersListColumnProps {
  role: 'tutor' | 'student' | 'all-organizers';
  /** Organizer filter context: 'session' (tutors+volunteers) or 'event' (+ coordinators). Inferred from role when omitted. */
  filterContext?: 'session' | 'event';
  emptyMessage?: string;
  excludedIds?: string[];
  pageSize?: number;
  getMatchCount?: (userId: string) => number;
  onDoubleClick?: (user: UserWithProfile) => void;
  onCreateClick?: () => void;
  className?: string;
  initialEstablishmentId?: string;
  initialEstablishmentName?: string;
  /** When true, all cards in this column show cursor-not-allowed and a tooltip. */
  dragDisabled?: boolean;
  /** Tooltip text shown when dragDisabled is true. */
  dragDisabledTooltip?: string;
  /**
   * Filter listed users to those with a presence day matching the session weekday
   * (0=Sunday .. 6=Saturday). When unset, no day filter is applied.
   * Independent of session start/end time (presence is by day, not by slot).
   */
  sessionAvailabilityDay?: number;
}

/** Role shown by this matching column (tutors, students, or organizers). */
type ColumnRole = UsersListColumnProps['role'];

/** Whether this column lists students rather than organizers. */
function isStudentColumn(role: ColumnRole): boolean {
  return role === 'student';
}

/** Default: organizers → event filters; tutors → session filters. */
function resolveFilterContext(
  filterContext: 'session' | 'event' | undefined,
  role: ColumnRole,
): 'session' | 'event' {
  if (filterContext) return filterContext;
  return role === 'all-organizers' ? 'event' : 'session';
}

/** Title / search copy for the matching column. */
function getColumnLabels(role: ColumnRole) {
  if (role === 'student') {
    const singular = getRoleLabel('student').toLowerCase();
    const plural = getRoleLabelPlural('student').toLowerCase();
    return {
      title: getRoleLabelPlural('student'),
      singularLabel: singular,
      pluralLabel: plural,
      placeholder: `Rechercher un ${singular}...`,
      emptyFallback: `Aucun ${singular} disponible`,
    };
  }
  if (role === 'all-organizers') {
    return {
      title: 'Organisateurs',
      singularLabel: 'organisateur',
      pluralLabel: 'organisateurs',
      placeholder: 'Rechercher un organisateur...',
      emptyFallback: 'Aucun organisateur disponible',
    };
  }
  const singular = getRoleLabel('tutor').toLowerCase();
  const plural = getRoleLabelPlural('tutor').toLowerCase();
  return {
    title: getRoleLabelPlural('tutor'),
    singularLabel: singular,
    pluralLabel: plural,
    placeholder: `Rechercher un ${singular}...`,
    emptyFallback: `Aucun ${singular} disponible`,
  };
}

/** Reset student filters, keeping the parent establishment when set. */
function emptyStudentFilters(establishmentId?: string): StudentFilters {
  return {
    search: undefined,
    statuses: [],
    levelIds: [],
    subjects: [],
    establishmentId,
    classIds: [],
    specialtyIds: [],
    accompanimentTypes: [],
    createdFrom: undefined,
    createdTo: undefined,
    availability: [],
  };
}

/** Reset organizer filters, keeping the parent establishment when set. */
function emptyOrganizerFilters(establishmentId?: string): OrganizerFilters {
  return { ...DEFAULT_ORGANIZER_FILTERS, establishmentId };
}

/** Union session weekday + manual day keys for the users-search API. */
function mergeAvailabilityDays(
  manualDays: string[] | undefined,
  sessionAvailabilityDay?: number,
): number[] | undefined {
  const fromKeys = availabilityKeysToDays(manualDays);
  const sessionDay = sessionAvailabilityDay == null ? [] : [sessionAvailabilityDay];
  const merged = [...new Set([...sessionDay, ...fromKeys])];
  return merged.length > 0 ? merged : undefined;
}

/** True when any organizer filter differs from the empty/default establishment. */
function hasOrganizerFiltersActive(
  filters: OrganizerFilters,
  initialEstablishmentId?: string,
): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.roleCodes.length > 0 ||
    filters.availability.length > 0 ||
    filters.subjects.length > 0 ||
    filters.engagementStartDateFrom !== undefined ||
    filters.engagementStartDateTo !== undefined ||
    filters.exitDateFrom !== undefined ||
    filters.exitDateTo !== undefined ||
    filters.exitReason !== undefined ||
    (filters.establishmentId !== undefined && filters.establishmentId !== initialEstablishmentId)
  );
}

/** True when any student filter differs from the empty/default establishment. */
function hasStudentFiltersActive(
  filters: StudentFilters,
  initialEstablishmentId?: string,
): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.levelIds.length > 0 ||
    filters.subjects.length > 0 ||
    (filters.availability != null && filters.availability.length > 0) ||
    (filters.establishmentId !== undefined && filters.establishmentId !== initialEstablishmentId) ||
    (filters.classIds != null && filters.classIds.length > 0) ||
    (filters.specialtyIds != null && filters.specialtyIds.length > 0) ||
    filters.createdFrom !== undefined ||
    filters.createdTo !== undefined
  );
}

/** Searchable column of users for pairing and matching. */
export const UsersListColumn: React.FC<UsersListColumnProps> = ({
  role,
  filterContext,
  emptyMessage,
  excludedIds = [],
  pageSize = 5,
  getMatchCount,
  onDoubleClick,
  onCreateClick,
  className,
  initialEstablishmentId,
  initialEstablishmentName,
  dragDisabled = false,
  dragDisabledTooltip,
  sessionAvailabilityDay,
}) => {
  const resolvedFilterContext = resolveFilterContext(filterContext, role);
  const { title, singularLabel, pluralLabel, placeholder, emptyFallback } = getColumnLabels(role);
  const [showFiltersModal, setShowFiltersModal] = React.useState(false);
  const [showConfigure, setShowConfigure] = React.useState(false);

  const [organizerFilters, setOrganizerFilters] = React.useState<OrganizerFilters>(() =>
    emptyOrganizerFilters(initialEstablishmentId || undefined),
  );

  const [studentFilters, setStudentFilters] = React.useState<StudentFilters>(() =>
    emptyStudentFilters(initialEstablishmentId || undefined),
  );

  // Keep establishment in sync when the parent form changes it
  React.useEffect(() => {
    if (isStudentColumn(role)) {
      setStudentFilters((prev) => ({
        ...prev,
        establishmentId: initialEstablishmentId || undefined,
        classIds: [],
        specialtyIds: [],
      }));
      return;
    }
    setOrganizerFilters((prev) => ({
      ...prev,
      establishmentId: initialEstablishmentId || undefined,
    }));
  }, [initialEstablishmentId, role]);

  const filters = isStudentColumn(role) ? studentFilters : organizerFilters;

  /** Session weekday plus any chip-selected days. */
  const availabilityDays = React.useMemo(
    () =>
      mergeAvailabilityDays(
        isStudentColumn(role) ? studentFilters.availability : organizerFilters.availability,
        sessionAvailabilityDay,
      ),
    [sessionAvailabilityDay, organizerFilters.availability, studentFilters.availability, role],
  );

  const { users, isLoading, page, total, hasMore, onNextPage, onPrevPage, onSearch, searchQuery } =
    useUsersSearch({
      role,
      excludedIds,
      pageSize,
      filters,
      availabilityDays,
    });

  // RBAC + preferences (filter visibility). Both hooks run unconditionally (React rules of hooks).
  const tutorFilterVisibility = useFilterVisibility<MatchingTutorFilterId>({
    preferencesName: 'tutors-matching',
    elementsName: 'tutors',
    allFilterGroupIds: [...MATCHING_TUTOR_FILTER_IDS],
  });

  const studentFilterVisibility = useFilterVisibility<MatchingStudentFilterId>({
    preferencesName: 'students-matching',
    elementsName: 'students',
    allFilterGroupIds: [...MATCHING_STUDENT_FILTER_IDS],
  });

  const {
    visibleFilterIds,
    allowedFilterIds,
    filterVisibility,
    handleToggleFilterVisibility,
    isSavingFilterVisibility,
  } = isStudentColumn(role) ? studentFilterVisibility : tutorFilterVisibility;

  const filterGroupLabels = isStudentColumn(role)
    ? MATCHING_STUDENT_FILTER_LABELS
    : MATCHING_TUTOR_FILTER_LABELS;

  const configureContent = (
    <FilterConfigurePanel
      allowedFilterIds={allowedFilterIds}
      filterVisibility={filterVisibility}
      filterGroupLabels={filterGroupLabels}
      onToggle={handleToggleFilterVisibility}
      isSaving={isSavingFilterVisibility}
    />
  );

  const toggleOrganizerStatus = React.useCallback((status: string) => {
    setOrganizerFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
  }, []);

  const setOrganizerEstablishment = React.useCallback((id: string | undefined) => {
    setOrganizerFilters((prev) => ({ ...prev, establishmentId: id }));
  }, []);

  const toggleOrganizerRoleCode = React.useCallback((code: string) => {
    setOrganizerFilters((prev) => ({
      ...prev,
      roleCodes: prev.roleCodes.includes(code)
        ? prev.roleCodes.filter((c) => c !== code)
        : [...prev.roleCodes, code],
    }));
  }, []);

  const toggleOrganizerAvailability = React.useCallback((day: string) => {
    setOrganizerFilters((prev) => ({
      ...prev,
      availability: prev.availability.includes(day)
        ? prev.availability.filter((d) => d !== day)
        : [...prev.availability, day],
    }));
  }, []);

  const toggleOrganizerSubject = React.useCallback((subjectId: string) => {
    setOrganizerFilters((prev) => ({
      ...prev,
      subjects: prev.subjects.includes(subjectId)
        ? prev.subjects.filter((s) => s !== subjectId)
        : [...prev.subjects, subjectId],
    }));
  }, []);

  const setOrganizerEngagementStartDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      setOrganizerFilters((prev) => ({
        ...prev,
        engagementStartDateFrom: from,
        engagementStartDateTo: to,
      }));
    },
    [],
  );

  const setOrganizerEngagementEndDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      setOrganizerFilters((prev) => ({
        ...prev,
        engagementEndDateFrom: from,
        engagementEndDateTo: to,
      }));
    },
    [],
  );

  const setOrganizerExitDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      setOrganizerFilters((prev) => ({ ...prev, exitDateFrom: from, exitDateTo: to }));
    },
    [],
  );

  const setOrganizerExitReason = React.useCallback((reason: string | undefined) => {
    setOrganizerFilters((prev) => ({ ...prev, exitReason: reason }));
  }, []);

  const toggleStudentStatus = React.useCallback((status: string) => {
    setStudentFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
  }, []);

  const toggleStudentGradeLevel = React.useCallback((levelId: string) => {
    setStudentFilters((prev) => ({
      ...prev,
      levelIds: prev.levelIds.includes(levelId)
        ? prev.levelIds.filter((l) => l !== levelId)
        : [...prev.levelIds, levelId],
    }));
  }, []);

  const toggleStudentSubject = React.useCallback((subjectId: string) => {
    setStudentFilters((prev) => ({
      ...prev,
      subjects: prev.subjects.includes(subjectId)
        ? prev.subjects.filter((s) => s !== subjectId)
        : [...prev.subjects, subjectId],
    }));
  }, []);

  const setStudentEstablishment = React.useCallback((id: string | undefined) => {
    setStudentFilters((prev) => ({ ...prev, establishmentId: id }));
  }, []);

  const setStudentClass = React.useCallback((classId: string | undefined) => {
    setStudentFilters((prev) => ({
      ...prev,
      classIds: classId ? [classId] : [],
    }));
  }, []);

  const setStudentSpecialties = React.useCallback((ids: string[]) => {
    setStudentFilters((prev) => ({ ...prev, specialtyIds: ids }));
  }, []);

  const setStudentDateRange = React.useCallback((from: Date | undefined, to: Date | undefined) => {
    setStudentFilters((prev) => ({ ...prev, createdFrom: from, createdTo: to }));
  }, []);

  const toggleStudentIsMinor = React.useCallback((value: boolean | undefined) => {
    setStudentFilters((prev) => ({ ...prev, isMinor: value }));
  }, []);

  const toggleStudentHasSpecialNeeds = React.useCallback((value: boolean | undefined) => {
    setStudentFilters((prev) => ({ ...prev, hasSpecialNeeds: value }));
  }, []);

  const toggleStudentAvailability = React.useCallback((day: string) => {
    setStudentFilters((prev) => ({
      ...prev,
      availability: prev.availability?.includes(day)
        ? prev.availability.filter((d) => d !== day)
        : [...(prev.availability || []), day],
    }));
  }, []);

  const clearFilters = React.useCallback(() => {
    const establishmentId = initialEstablishmentId || undefined;
    if (isStudentColumn(role)) {
      setStudentFilters(emptyStudentFilters(establishmentId));
      return;
    }
    setOrganizerFilters(emptyOrganizerFilters(establishmentId));
  }, [role, initialEstablishmentId]);

  const hasActiveFilters = isStudentColumn(role)
    ? hasStudentFiltersActive(studentFilters, initialEstablishmentId)
    : hasOrganizerFiltersActive(organizerFilters, initialEstablishmentId);

  const initialEstablishmentOption = React.useMemo(
    () =>
      initialEstablishmentId && initialEstablishmentName
        ? { value: initialEstablishmentId, label: initialEstablishmentName }
        : undefined,
    [initialEstablishmentId, initialEstablishmentName],
  );

  /** Single custom filter group: student vs organizer panel. */
  const filterGroups = React.useMemo(() => {
    if (!isStudentColumn(role)) {
      return [
        {
          id: 'filters' as const,
          label: 'Filtres',
          type: 'custom' as const,
          customContent: (
            <OrganizerFiltersContent
              filters={organizerFilters}
              context={resolvedFilterContext}
              initialEstablishmentOption={initialEstablishmentOption}
              visibleFilterIds={visibleFilterIds}
              sessionAvailabilityDay={sessionAvailabilityDay}
              onToggleStatus={toggleOrganizerStatus}
              onSetEstablishment={setOrganizerEstablishment}
              onToggleRoleCode={toggleOrganizerRoleCode}
              onToggleAvailability={toggleOrganizerAvailability}
              onToggleSubject={toggleOrganizerSubject}
              onSetEngagementStartDateRange={setOrganizerEngagementStartDateRange}
              onSetEngagementEndDateRange={setOrganizerEngagementEndDateRange}
              onSetExitDateRange={setOrganizerExitDateRange}
              onSetExitReason={setOrganizerExitReason}
            />
          ),
        },
      ];
    }
    return [
      {
        id: 'filters' as const,
        label: 'Filtres',
        type: 'custom' as const,
        customContent: (
          <StudentFiltersContent
            filters={studentFilters}
            allowedFilterIds={allowedFilterIds}
            visibleFilterIds={visibleFilterIds}
            sessionAvailabilityDay={sessionAvailabilityDay}
            onToggleStatus={toggleStudentStatus}
            onToggleGradeLevel={toggleStudentGradeLevel}
            onToggleSubject={toggleStudentSubject}
            onSetEstablishment={setStudentEstablishment}
            onSetClass={setStudentClass}
            onSetSpecialties={setStudentSpecialties}
            onSetCustomDateRange={setStudentDateRange}
            onToggleIsMinor={toggleStudentIsMinor}
            onToggleHasSpecialNeeds={toggleStudentHasSpecialNeeds}
            onToggleAvailability={toggleStudentAvailability}
            initialEstablishmentOption={initialEstablishmentOption}
          />
        ),
      },
    ];
  }, [
    role,
    resolvedFilterContext,
    organizerFilters,
    studentFilters,
    allowedFilterIds,
    visibleFilterIds,
    sessionAvailabilityDay,
    toggleOrganizerStatus,
    setOrganizerEstablishment,
    toggleOrganizerRoleCode,
    toggleOrganizerAvailability,
    toggleOrganizerSubject,
    setOrganizerEngagementStartDateRange,
    setOrganizerEngagementEndDateRange,
    setOrganizerExitDateRange,
    setOrganizerExitReason,
    toggleStudentStatus,
    toggleStudentGradeLevel,
    toggleStudentSubject,
    setStudentEstablishment,
    setStudentClass,
    setStudentSpecialties,
    setStudentDateRange,
    toggleStudentIsMinor,
    toggleStudentHasSpecialNeeds,
    toggleStudentAvailability,
    initialEstablishmentOption,
  ]);

  const Icon = isStudentColumn(role) ? User : GraduationCap;
  const colorVar = isStudentColumn(role) ? '--color-3-nuance-1' : '--color-2-nuance-1';
  const totalPages = Math.ceil(total / pageSize);

  /** Keep last known totals while the next page is loading (avoids header flicker). */
  const lastTotalRef = React.useRef(total);
  const lastTotalPagesRef = React.useRef(totalPages);
  if (!isLoading) {
    lastTotalRef.current = total;
    lastTotalPagesRef.current = totalPages;
  }
  const displayTotal = isLoading ? lastTotalRef.current : total;
  const displayTotalPages = isLoading ? lastTotalPagesRef.current : totalPages;

  return (
    <div className={`matching-column h-full ${className || ''}`}>
      <div className="matching-column-header">
        <div className="flex items-start justify-between w-full">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center border"
              style={{
                background: `color-mix(in srgb, var(${colorVar}) 10%, transparent)`,
                borderColor: `var(${colorVar})`,
              }}
            >
              <Icon className="w-4 h-4" style={{ color: `var(${colorVar})` }} />
            </div>
            <div className="leading-tight">
              <h3 className="text-base font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
                {title}
              </h3>
              <p className="text-xs text-muted-foreground leading-tight">
                {displayTotal} {displayTotal > 1 ? pluralLabel : singularLabel}
              </p>
              <p className="text-xs text-muted-foreground leading-tight">
                Page {page}/{displayTotalPages}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowFiltersModal(true)}
                icon={Filter}
                tooltipText="Filtres"
                tooltipSide="bottom"
                showBadge={hasActiveFilters}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={onCreateClick}
                icon={Plus}
                tooltipText={`Créer un ${singularLabel}`}
                tooltipSide="bottom"
                className="matching-action-button"
              />
            </div>
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={onPrevPage}
                disabled={page === 1}
                icon={ChevronLeft}
                tooltipText="Page précédente"
                tooltipSide="bottom"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={onNextPage}
                disabled={!hasMore}
                icon={ChevronRight}
                tooltipText="Page suivante"
                tooltipSide="bottom"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="matching-column-content">
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 mb-3">
            <SearchInput
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              onClear={() => onSearch('')}
              size="sm"
              variant="outlined"
              showShortcutHint={false}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-[var(--color-1-nuance-3)] scrollbar-track-transparent">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" label="Chargement..." />
              </div>
            )}

            {!isLoading && users.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center border"
                  style={{
                    background: `color-mix(in srgb, var(${colorVar}) 10%, transparent)`,
                    borderColor: `var(${colorVar})`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: `var(${colorVar})` }} />
                </div>
                <p className="text-sm text-muted-foreground">{emptyMessage || emptyFallback}</p>
              </div>
            )}

            {!isLoading && users.length > 0 && (
              <div className="space-y-2 pt-1">
                {users.map((user) => (
                  <DraggableUserCard
                    key={user.id}
                    user={user}
                    role={role}
                    Icon={Icon}
                    colorVar={colorVar}
                    matchCount={getMatchCount?.(user.id)}
                    onDoubleClick={onDoubleClick}
                    dragDisabled={dragDisabled}
                    dragDisabledTooltip={dragDisabledTooltip}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <DataTableFilters
        show={showFiltersModal}
        mode="modal"
        onClose={() => setShowFiltersModal(false)}
        hasActiveFilters={hasActiveFilters}
        filterGroups={filterGroups}
        onClearFilters={clearFilters}
        resultCount={total}
        resultLabel={pluralLabel}
        onToggleConfigure={
          allowedFilterIds.length > 0 ? () => setShowConfigure((v) => !v) : undefined
        }
        showConfigure={showConfigure}
        configureContent={configureContent}
      />
    </div>
  );
};

/** Props for {@link DraggableUserCard}. */
interface DraggableUserCardProps {
  user: UserWithProfile;
  role: 'tutor' | 'student' | 'all-organizers';
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  colorVar: string;
  matchCount?: number;
  onDoubleClick?: (user: UserWithProfile) => void;
  dragDisabled?: boolean;
  dragDisabledTooltip?: string;
}

/** Sortable user card; drag payload carries `roleGroup` + user. */
const DraggableUserCard: React.FC<DraggableUserCardProps> = ({
  user,
  role,
  Icon,
  colorVar,
  matchCount,
  onDoubleClick,
  dragDisabled = false,
  dragDisabledTooltip,
}) => {
  const roleLabel = getPrimaryRole(user.userRoles)?.label || null;

  /** Fallback roleGroup for the drag payload when the user has no roles yet. */
  const defaultRoleGroup = role === 'student' ? 'students' : 'tutors';
  const roleGroup = getUserRoleGroup(user) || defaultRoleGroup;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: user.id,
    data: { roleGroup, user },
    disabled: dragDisabled,
  });

  /** Optional assign-on-double-click (parent matching form). */
  const handleDoubleClick = React.useCallback(() => {
    if (onDoubleClick) {
      onDoubleClick(user);
    }
  }, [onDoubleClick, user]);

  const card = (
    <div
      ref={setNodeRef}
      {...(dragDisabled ? {} : listeners)}
      {...attributes}
      onDoubleClick={handleDoubleClick}
      className={cn(
        'matching-user-card',
        isDragging && 'matching-user-card-dragging',
        dragDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="flex items-center gap-2 flex-1">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center border flex-shrink-0"
          style={{
            background: `color-mix(in srgb, var(${colorVar}) 15%, transparent)`,
            borderColor: `var(${colorVar})`,
          }}
        >
          <Icon className="w-4 h-4" style={{ color: `var(${colorVar})` }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">
            {role === 'student'
              ? user.displayName || user.email || 'Utilisateur'
              : `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                user.displayName ||
                user.email ||
                'Utilisateur'}
          </p>
          {roleLabel && (
            <span
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none mt-0.5"
              style={{
                background: `color-mix(in srgb, var(${colorVar}) 15%, transparent)`,
                color: `var(${colorVar})`,
                border: `1px solid color-mix(in srgb, var(${colorVar}) 35%, transparent)`,
              }}
            >
              {roleLabel}
            </span>
          )}
        </div>
        {role !== 'student' && matchCount !== undefined && matchCount > 0 && (
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0"
            style={{
              background: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
              color: 'var(--color-3-nuance-1)',
              borderColor: 'var(--color-3-nuance-1)',
            }}
          >
            {matchCount}
          </span>
        )}
      </div>
    </div>
  );

  if (dragDisabled && dragDisabledTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{card}</TooltipTrigger>
          <TooltipContent side="left" variant="dark">
            {dragDisabledTooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return card;
};
